import Joi from "@hapi/joi";
import { IControl } from "types/app";
import { SelectEmptyValue } from "Consts";
import { isNullOrUndefined } from "utils";
export const instanceNameRegex = /^[a-zA-Z0-9\-._\\]{3,150}$/;
export const lettersNumbersExtended = /^[a-zA-Z0-9 ()\\.\-_\n@]*$/;
export const letters = /^[a-zA-Z\-_]*$/;
export const lastNameRegex = /^[a-zA-Z0-9 ()\-_\n]*$/;
export const cityRegex = /^([a-zA-Z\u0080-\u024F]+(?:. |-| |'))*[a-zA-Z\u0080-\u024F]*$/;
export const numericRegex = /^[0-9]+$/;
export const moneyRegex = /^\d+(\.\d+)?$/;
export const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$/;
export const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
export const usCanadaZipCode =
  /^(\d{5}(-\d{4})?|[A-CEGHJ-NPRSTVXY]\d[A-CEGHJ-NPRSTV-Z] ?\d[A-CEGHJ-NPRSTV-Z]\d)$/;
export const collectorRegex = /^[a-zA-Z0-9 ()_-]{3,100}$/;
const accountNameRegex = /^[a-zA-Z0-9 ()_-]{2,100}$/;
export const emailRegex = /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
export const officePhoneRegex =
  /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
const accountDisplayRegex = /^[a-zA-Z0-9 ()_-]{2,100}$/;
export const keyValuePairRegex = /^([\w\s]+)\s?:\s?([\w\s]+)$/;
const domainAccountRegex = /^(?:\\[a-zA-Z0-9 ._-]+|[a-zA-Z0-9 ._-]+\\[a-zA-Z0-9 ._-]+)$/;
export const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const validateServersList = (value: any, helpers: any) => {
  const servers = value
    .split(/[\n,; ]/)
    .map((s: string) => s?.replaceAll(" ", "")) //todo: replace other chars as well?
    .filter((s: string) => s && s.length > 0);

  const invalid: string[] = [];

  servers.forEach((server: any) => {
    if (!instanceNameRegex.test(server)) {
      invalid.push(server);
    }
  });
  if (invalid.length > 0) {
    throw new Error(ValidationErrors.serversList);
  }

  return value;
};

export const ValidationErrors = {
  firstName: "First Name must contain letters only and be a minimum of 2 characters.",
  lastName: "Last Name must contain letters only and be a minimum of 2 characters.",
  preferredName: "Name must contain letters only and be a minimum of 2 characters.",
  userTitle: "Title must contain letters only and be a minimum of 2 characters.",
  city: "Please provide a valid city.",
  email: "Please provide a valid email.",
  phone: "Please provide a valid phone number.",
  zip: "Please provide a valid zip code.",
  password:
    "Password must be at least 11 characters long, and contain at least 1 lowercase, 1 uppercase and 1 number.",
  alphanumericextended: "Please use only letters and numbers, 3 character minimum.",
  url: "Please provide a valid URL.",
  time: "Please enter a time in the format HH:MM:SS.",
  collector: "Collector name must be 3 in length and should not include special characters.",
  serversList: "Server name must be 3 to 150 in length and should not include special characters.",
  accountDisplayName: "Display name must be 2 in length and should not include special characters.",
  accountName: "Account name must be 2 in length and should not include special characters.",
  domainAccount:
    "Please follow the correct format: either '.\\username' (local user) or 'domain\\username'",
  guidRegex: "Please enter a valid GUID.",
};

const getRegexValidationRule = (regExp: RegExp, message: string, min: number, max: number) => {
  return Joi.string().min(min).max(max).regex(regExp).error(new Error(message));
};

class Validation {
  constructor() {
    // @ts-ignore
    this.rules = {
      firstName: getRegexValidationRule(letters, ValidationErrors.firstName, 2, 50),
      lastName: getRegexValidationRule(lastNameRegex, ValidationErrors.lastName, 2, 100),
      preferredName: getRegexValidationRule(
        lettersNumbersExtended,
        ValidationErrors.preferredName,
        2,
        100
      ),
      title: Joi.string().max(200),
      userTitle: Joi.string().max(200),
      city: getRegexValidationRule(cityRegex, ValidationErrors.city, 3, 50), // Joi.string().alphanum().min(3).max(30).error(new Error(errors.city)),
      email: Joi.string()
        .email({ tlds: { allow: false } })
        .max(256)
        .regex(emailRegex)
        .error(new Error(ValidationErrors.email)),
      alphanumericextended: Joi.string()
        .min(3)
        .max(100)
        .regex(lettersNumbersExtended, "Alphanumeric")
        .error(new Error(ValidationErrors.alphanumericextended)),
      alphanumericlimited: Joi.string()
        .min(3)
        .max(30) // should be removed as can be controlled via html- https://www.w3schools.com/tags/att_input_maxlength.asp
        .regex(lettersNumbersExtended, "Alphanumeric"),
      // alphanumeric: Joi.string().alphanum().min(3).max(30),
      phone: Joi.string()
        .replace(/\(/, "")
        .replace(/\)/, "")
        .replace(/\s/g, "")
        .replace("-", "")
        .length(10)
        .regex(numericRegex)
        .error(new Error(ValidationErrors.phone)),
      linePhone: Joi.string()
        .replace(/\(/, "")
        .replace(/\)/, "")
        .replace(/\s/g, "")
        .replace(/-/g, "")
        .min(10)
        .max(16)
        .regex(officePhoneRegex)
        .error(new Error(ValidationErrors.phone)),
      numeric: Joi.number().min(0),
      positiveNumber: Joi.number().min(1),
      zip: getRegexValidationRule(usCanadaZipCode, ValidationErrors.zip, 2, 50),
      url: Joi.string().uri().error(new Error(ValidationErrors.url)),
      time: Joi.string().regex(timeRegex).error(new Error(ValidationErrors.time)),
      password: getRegexValidationRule(passwordRegex, ValidationErrors.password, 11, 100),
      collector: getRegexValidationRule(collectorRegex, ValidationErrors.collector, 3, 100),
      accountDisplayName: getRegexValidationRule(
        accountDisplayRegex,
        ValidationErrors.accountDisplayName,
        2,
        100
      ),
      accountName: getRegexValidationRule(accountNameRegex, ValidationErrors.accountName, 2, 100),
      serversList: Joi.string()
        .min(3)
        .custom(validateServersList)
        .error(new Error(ValidationErrors.serversList)),
      domainAccount: Joi.string()
        .regex(domainAccountRegex)
        .error(new Error(ValidationErrors.domainAccount)),
      guid: Joi.string().pattern(guidRegex).required().messages({
        "string.pattern.base": ValidationErrors.guidRegex,
      }),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  isEmptyValue(control: any, val: any) {
    switch (control.inputType) {
      case "select":
        return val === undefined || val === null || val === SelectEmptyValue;

      default:
        return val === undefined || val === null || `${val}`.trim().length === 0;
    }
  }

  validateControl(control: IControl, input: any, source: any = {}) {
    if (control.required) {
      let isVisible = true;
      if (control.visibleIf) {
        isVisible = control.visibleIf(source);
      }
      if (isVisible && this.isEmptyValue(control, input)) {
        // @ts-ignore
        return `${control.label?.trim() || "Field"} is required.`;
      }
    }

    if (Array.isArray(control.fileTypes) && input.type) {
      // older files will not have type
      if (!control.fileTypes.map((ft) => ft.type).includes(input.type)) {
        return `Allowed filed type(s): ${control.fileTypes.map((ft) => ft.name).join(",")}`;
      }
    }

    if (control.validation && input) {
      const validationRule = this.getValidationRule(control.validation);

      if (validationRule) {
        const validationObject = {
          // @ts-ignore
          [control.field || control.label]: control.inputType === "duration" ? input.val : input, //todo: generify
        };

        const schema = Joi.object({
          // @ts-ignore
          [control.field || control.label]: validationRule,
        });

        const validationResult = schema.validate(validationObject);

        if (validationResult.error) {
          if (control.label) {
            validationResult.error.message = validationResult.error.message.replace(
              `"${control.field}"`,
              `"${control.label}"`
            );
          }

          return validationResult.error.message;
        }
        return null;
      }
    }

    return null;
  }

  validateControls(controls: IControl[], data: any = {}) {
    const validations: any = {};

    (controls || []).forEach((control) => {
      const errorMessage = this.validateControl(control, data[control.field], data);
      validations[control.field] = errorMessage;

      if (!validations[control.field] && control.mustMatchExactField) {
        if (data[control.mustMatchExactField] !== data[control.field]) {
          validations[control.field] = `${
            controls.find((c) => c.field === control.mustMatchExactField)?.label
          } and ${control.label} do not match.`;
        }
      }
    });

    return validations;
  }

  validateTaskParameter(parameter: any) {
    if (Array.isArray(parameter)) {
      // 1 of x params is required is required
      if (!parameter.some((p) => !isNullOrUndefined(p.value) && `${p.value}`.trim().length > 0)) {
        return `One of [${parameter.map((p) => p.displayName || p.name).join(",")}] is required.`;
      }
    } else {
      const required = parameter?.meta?.required ?? false;
      if (required) {
        if (isNullOrUndefined(parameter.value) || `${parameter.value}`.trim().length === 0) {
          return `Variable [${parameter.displayName || parameter.name}] is required.`;
        }
      }
    }
  }

  validateTaskParameters(parametersList: any) {
    const validations: any = {};

    (parametersList || []).forEach((parameter: any) => {
      const errorMessage = this.validateTaskParameter(parameter);
      validations[
        Array.isArray(parameter)
          ? parameter.map((p) => p.displayName || p.name).join(",")
          : parameter.name
      ] = errorMessage;
    });

    return validations;
  }

  getValidationRule(name: string) {
    // @ts-ignore
    return this.rules[name];
  }
}

const instance = new Validation();
export default instance;
